
import { supabase } from '@/integrations/supabase/client';
import { authService } from '@/services/authService';
import { UserRole } from '@/types/auth';
import { authLogger } from '@/utils/debug';

export const authOperations = {
  async signIn(email: string, password: string) {
    authLogger.log('🔥 Attempting to sign in with:', email);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        authLogger.error('🔥 Sign in error:', error);
        authLogger.captureError(error);
        throw error;
      }

      // Simplified validation after successful sign in
      if (data.user) {
        authLogger.log('🔥 Sign in successful, validating user access...');
        const validation = await authService.validateUserAccess(data.user.id);
        
        if (!validation.isValid) {
          authLogger.log('🔥 User access denied after sign in:', validation.reason);
          await supabase.auth.signOut();
          throw new Error(validation.reason || 'Account is inactive');
        }

        // Update last login timestamp
        authLogger.log('🔥 Updating last login timestamp for user:', data.user.id);
        await authService.updateLastLoginTimestamp(data.user.id);
      }

      authLogger.log('🔥 Sign in completed successfully');
    } catch (error) {
      authLogger.error('🔥 Sign in process failed:', error);
      authLogger.captureError(error);
      throw error;
    }
  },

  async signUp(email: string, password: string, fullName: string, role: UserRole = 'client') {
    authLogger.log('🔥 Attempting to sign up with:', { email, fullName, role });
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: role,
          },
        },
      });

      if (error) {
        authLogger.error('🔥 Sign up error:', error);
        authLogger.captureError(error);
        throw error;
      }

      authLogger.log('🔥 Sign up successful:', data);
    } catch (error) {
      authLogger.error('🔥 Sign up process failed:', error);
      authLogger.captureError(error);
      throw error;
    }
  },

  async signOut() {
    authLogger.log('🔥 Signing out...');
    
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        authLogger.error('🔥 Sign out error:', error);
        authLogger.captureError(error);
        throw error;
      }
      authLogger.log('🔥 Sign out successful');
    } catch (error) {
      authLogger.error('🔥 Sign out process failed:', error);
      authLogger.captureError(error);
      throw error;
    }
  },

  async resetPassword(email: string) {
    authLogger.log('🔥 Resetting password for:', email);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        authLogger.error('🔥 Reset password error:', error);
        authLogger.captureError(error);
        throw error;
      }
      authLogger.log('🔥 Reset password email sent');
    } catch (error) {
      authLogger.error('🔥 Reset password process failed:', error);
      authLogger.captureError(error);
      throw error;
    }
  }
};
